/* eslint-disable @typescript-eslint/no-explicit-any */
import { Flex, FormControlProps, InputProps, Text, useTheme, WithCSSVar, forwardRef, Box, theme } from '@chakra-ui/react';
import { Dict } from '@chakra-ui/utils';
import styled from '@emotion/styled/macro';
import { FC, useEffect, useRef, useState, ReactElement } from 'react';
import { compareAsc } from 'date-fns';
import GenericDatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import InputMask from 'react-input-mask';
import { ReactComponent as CalendarIcon } from 'assets/svgs/calendar.svg';
import { rem, rgba } from 'polished';
import { FormControl, Label } from 'components';
import Input from 'components/Input/Corporate/Input';
import enGb from 'date-fns/locale/en-GB';
registerLocale('en-gb', enGb);

const CalendarBox = styled(Flex)<{ primaryColor: string }>`
  position: relative;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker-popper {
    z-index: 999;
  }

  .react-datepicker-popper[data-placement^='bottom'] {
    margin-top: 0;
  }

  .chooose-picker button:focus {
    box-shadow: none;
  }

  .react-datepicker__close-icon::after {
    width: 24px;
    background: transparent;
    font-size: ${rem(32)};
    color: ${(props): string => props.primaryColor};
    padding-top: 5px;
  }

  .chooose-calendar {
    background: ${theme.colors.white};
    border-radius: 8px;
    box-shadow: 0px 14px 28px ${rgba(theme.colors.gray[500], 0.08)};
    border: none;

    .react-datepicker__month-container {
      border-radius: 8px;
    }

    .react-datepicker__header {
      background: transparent;
      border-bottom: none;
      border-radius: 8px;
      padding-top: 16px;
    }

    .react-datepicker__month {
      margin: 0 6px 11px 6px;
    }

    .react-datepicker__current-month {
      color: ${theme.colors.gray[800]};
      font-weight: 600;
      font-size: ${rem(13)};
    }

    .react-datepicker__day-names {
      margin-top: 10px;
    }

    .react-datepicker__navigation:not(.react-datepicker__navigation--years) {
      top: 19px;
      width: 15px;
      height: 15px;
      border: none;

      &:before,
      &:after {
        content: '';
        position: absolute;
        left: 3px;
        top: 3px;
        background: ${theme.colors.gray[800]};
      }

      &:before {
        height: 10px;
        width: 2px;
      }

      &:after {
        height: 2px;
        width: 10px;
      }

      &:focus {
        outline: none;
      }
    }

    .react-datepicker__navigation--previous {
      left: 20px;
      transform: rotate(-45deg);
    }

    .react-datepicker__navigation--next {
      right: 20px;
      transform: rotate(135deg);
    }

    .react-datepicker__day-name {
      color: ${theme.colors.gray[400]};
      font-weight: 500;
      font-size: ${rem(12)};
      line-height: 34px;
      width: 34px;
      text-align: center;
      margin: 2px 5px;
    }

    .react-datepicker__day--keyboard-selected {
      background: transparent;
      color: ${theme.colors.gray[800]};
    }

    .react-datepicker__day {
      line-height: 25px;
      width: 26px;
      font-size: ${rem(13)};
      font-weight: 500;
      color: ${theme.colors.gray[800]};
      margin: 5px 9px;
      border-radius: 6px;

      &:focus {
        outline: none;
      }
    }

    .react-datepicker__day.react-datepicker__day--disabled {
      opacity: 0.3;
      cursor: default;
    }

    .react-datepicker__day--selected,
    .react-datepicker__day--keyboard-selected:hover {
      background: ${theme.colors.pink[500]};
      color: ${theme.colors.white};
    }

    .react-datepicker__header__dropdown {
      margin-top: 8px;
    }

    .react-datepicker__month-read-view--selected-month,
    .react-datepicker__year-read-view--selected-year {
      padding-right: 5px;
    }
  }
`;

const LabelText = styled(Text)`
  margin: 0;
  padding: 0;
  width: 100%;
  margin-bottom: 5px;
`;

const StyledIcon = styled(CalendarIcon)`
  position: absolute;
  top: 23%;
  right: 15px;
  width: 24px;
  min-width: 24px;
  z-index: 1;
  pointer-events: none;
`;

const Wrapper = styled(Label)<{ theme: WithCSSVar<Dict<any>> }>(
  ({ theme }) => `
  &[data-focus] > svg {
    fill: ${theme.colors.primary[400]};
  }
`,
);

export interface DatePickerProps {
  elementId?: string;
  ariaLabel?: string;
  formLabel?: string;
  formLabelFont?: string;
  formLabelColor?: string;
  inputProps?: Partial<InputProps>;
  showMonthDropdown?: boolean;
  showYearDropdown?: boolean;
  controlProps?: FormControlProps;
  minDate?: Date;
  initialDate?: Date;
  isClearable?: boolean;
  language?: string;
  onChange?: (date: Date | Date[]) => void;
  dateFormat?: string;
  inputMaskString?: string;
  placeholder?: string;
  selectsRange?: boolean;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  inline?: boolean;
  selected?: Date;
  startDate?: Date;
  endDate?: Date;
  shouldCloseOnSelect?: boolean;
  popperPlacement?:
    | 'auto'
    | 'bottom'
    | 'top'
    | 'left'
    | 'right'
    | 'auto-start'
    | 'auto-end'
    | 'top-start'
    | 'top-end'
    | 'right-start'
    | 'right-end'
    | 'bottom-end'
    | 'bottom-start'
    | 'left-end'
    | 'left-start';
  noLabel?: boolean;
}

// eslint-disable-next-line react/display-name
const DatePicker: FC<DatePickerProps> = forwardRef(
  (
    {
      elementId,
      formLabel,
      formLabelFont,
      formLabelColor,
      inputProps,
      controlProps,
      showMonthDropdown,
      showYearDropdown,
      minDate,
      initialDate,
      isClearable,
      language,
      onChange,
      dateFormat = 'dd/MM/yyyy',
      inputMaskString = '99/99/9999',
      popperPlacement = 'bottom-end',
      placeholder,
      selectsRange,
      inline,
      selectsStart,
      selectsEnd,
      selected,
      startDate,
      endDate,
      shouldCloseOnSelect,
      noLabel,
    },
    ref,
  ) => {
    const [startDateValue, setStartDateValue] = useState<Date>();
    const [endDateValue, setEndDateValue] = useState<Date>();
    const calendarRef = useRef(null);
    const theme = useTheme();
    const { colors } = theme;

    const setDate = (date: Date | Date[]): void => {
      if (selectsRange && Array.isArray(date)) {
        const [start, end] = date;
        setStartDateValue(start);
        setEndDateValue(end);
      } else if (!Array.isArray(date)) {
        setStartDateValue(date);
      }
      onChange && onChange(date);
    };

    useEffect(() => {
      if (initialDate) {
        setStartDateValue(initialDate);
      }
    }, []);

    useEffect(() => {
      if (minDate && startDateValue && compareAsc(startDateValue, minDate) === -1) {
        setStartDateValue(minDate);
      }
    }, [minDate, startDateValue]);

    return (
      <FormControl {...controlProps}>
        <CalendarBox primaryColor={formLabelColor ?? colors.secondary[500]} theme={theme}>
          <Wrapper htmlFor={elementId ?? 'chooose-picker'} flexDirection="column" w="100%" p={0} pos="relative" theme={theme}>
            {!noLabel && (
              <LabelText fontFamily={formLabelFont ?? 'inherit'} color={formLabelColor ?? colors.secondary[500]}>
                {formLabel ?? 'Date'}
              </LabelText>
            )}
            <Box as="span" ref={ref} tabIndex={0}>
              <GenericDatePicker
                selected={selected || startDateValue}
                dateFormat={dateFormat}
                onChange={(date): void => setDate(date as Date)}
                customInput={
                  // @ts-ignore
                  <InputMask mask={inputMaskString} id={elementId ?? 'chooose-picker'} {...inputProps}>
                    {(props: unknown): ReactElement => <Input {...props} name={inputProps?.name} color={colors.secondary[900]} height="50px" />}
                  </InputMask>
                }
                showPopperArrow={false}
                popperPlacement={popperPlacement}
                calendarClassName="chooose-calendar"
                wrapperClassName="chooose-picker"
                formatWeekDay={(nameOfDay): string => nameOfDay.substr(0, 3)}
                scrollableYearDropdown={showYearDropdown}
                showMonthDropdown={showMonthDropdown}
                showYearDropdown={showYearDropdown}
                placeholderText={placeholder ?? dateFormat.toLowerCase()}
                minDate={minDate}
                isClearable={isClearable}
                locale={language ?? 'en-gb'}
                selectsRange={selectsRange}
                ref={calendarRef}
                startDate={startDate || startDateValue}
                endDate={endDate || endDateValue}
                inline={inline}
                selectsStart={selectsStart}
                selectsEnd={selectsEnd}
                shouldCloseOnSelect={shouldCloseOnSelect}
                popperModifiers={{
                  preventOverflow: {
                    enabled: true,
                  },
                }}
              />
            </Box>

            {!selectsRange && !selected && !startDateValue && <StyledIcon fill={colors.secondary[400]} />}
          </Wrapper>
        </CalendarBox>
      </FormControl>
    );
  },
);

export default DatePicker;
